import { useEffect, useState } from 'react'
import { useAuth } from '../auth'
import Layout  from '../components/Layout'
import { usersApi, User } from '../api/client'

const C = { teal: '#2B4C5E', yellow: '#F2B705', muted: '#6B8A91', light: '#C7D9DB' }

function fmt(iso: string) {
  return new Date(iso).toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: false,
  })
}

export default function UsersPage() {
  const { getToken, ready } = useAuth()
  const [users,   setUsers]   = useState<User[]>([])
  const [loading, setLoading] = useState(true)
  const [myRole,  setMyRole]  = useState<string>('')
  const [msg,     setMsg]     = useState('')
  const [isErr,   setIsErr]   = useState(false)
  const [saving,  setSaving]  = useState(false)

  const [form, setForm] = useState({ email: '', name: '', role: 'user' })

  useEffect(() => { if (ready) load() }, [ready])

  async function load() {
    setLoading(true)
    try {
      await getToken()
      const [usersRes, whoRes] = await Promise.all([usersApi.list(), usersApi.whoami()])
      setUsers(usersRes.data.users ?? [])
      setMyRole(whoRes.data.role)
    } catch (e: any) {
      flash(e?.response?.data?.detail || 'Failed to load users', true)
    } finally {
      setLoading(false)
    }
  }

  async function handleAdd(e: React.FormEvent) {
    e.preventDefault()
    if (!form.email || !form.name) { flash('Email and name are required', true); return }
    setSaving(true)
    try {
      await getToken()
      const r = await usersApi.add(form)
      setUsers(prev => [r.data, ...prev])
      setForm({ email: '', name: '', role: 'user' })
      flash(`${r.data.name} added`)
    } catch (e: any) {
      flash(e?.response?.data?.detail || 'Add failed', true)
    } finally {
      setSaving(false)
    }
  }

  async function handleRoleChange(email: string, role: string) {
    try {
      await getToken()
      await usersApi.setRole(email, role)
      setUsers(prev => prev.map(u => u.email === email ? { ...u, role: role as 'admin' | 'user' } : u))
      flash('Role updated')
    } catch (e: any) {
      flash(e?.response?.data?.detail || 'Update failed', true)
    }
  }

  async function handleRemove(email: string, name: string) {
    if (!confirm(`Remove ${name} (${email})?`)) return
    try {
      await getToken()
      await usersApi.remove(email)
      setUsers(prev => prev.filter(u => u.email !== email))
      flash(`${name} removed`)
    } catch (e: any) {
      flash(e?.response?.data?.detail || 'Remove failed', true)
    }
  }

  function flash(text: string, err = false) {
    setIsErr(err); setMsg(text); setTimeout(() => setMsg(''), 5000)
  }

  const isAdmin = myRole === 'admin'

  return (
    <Layout>
      <main style={{ padding: '24px 20px', minHeight: 'calc(100vh - 64px)' }}>

        {msg && (
          <div style={{
            position: 'fixed', top: 80, right: 24,
            background: isErr ? '#7f1d1d' : C.teal,
            color: '#fff', padding: '12px 20px', borderRadius: 8,
            borderLeft: `4px solid ${isErr ? '#ef4444' : C.yellow}`,
            zIndex: 2000, maxWidth: 420,
          }}>{msg}</div>
        )}

        {/* Header */}
        <div style={{ marginBottom: 32 }}>
          <h1 style={{ color: C.teal, fontSize: 24, fontWeight: 700 }}>Users</h1>
          <p style={{ color: C.muted, fontSize: 14, marginTop: 4 }}>
            Manage who can access the platform and schedule reports
          </p>
        </div>

        {/* Add user form — admin only */}
        {isAdmin && (
          <div style={{
            background: '#fff', borderRadius: 12, padding: 24,
            boxShadow: '0 1px 4px rgba(0,0,0,0.08)', marginBottom: 24,
          }}>
            <div style={{
              color: C.teal, fontWeight: 700, fontSize: 14,
              marginBottom: 16, paddingBottom: 12,
              borderBottom: `2px solid ${C.yellow}`, display: 'inline-block',
            }}>Add User</div>

            <form onSubmit={handleAdd} style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <div style={{ flex: '1 1 220px' }}>
                <label style={labelStyle}>Full Name</label>
                <input
                  value={form.name}
                  onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  placeholder="e.g. Rahul Sharma"
                  style={inputStyle}
                  required
                />
              </div>
              <div style={{ flex: '1 1 260px' }}>
                <label style={labelStyle}>Work Email</label>
                <input
                  type="email"
                  value={form.email}
                  onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                  placeholder="e.g. rahul.sharma@bnfl.in"
                  style={inputStyle}
                  required
                />
              </div>
              <div style={{ flex: '0 0 140px' }}>
                <label style={labelStyle}>Role</label>
                <select value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))} style={inputStyle}>
                  <option value="user">User</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              <button type="submit" disabled={saving} style={{
                background: C.yellow, color: C.teal, border: 'none',
                padding: '10px 24px', borderRadius: 6,
                fontSize: 14, fontWeight: 700, cursor: 'pointer',
                height: 40, flexShrink: 0,
              }}>
                {saving ? 'Adding…' : '+ Add'}
              </button>
            </form>

            <div style={{
              marginTop: 12, padding: '10px 14px',
              background: 'rgba(242,183,5,0.07)', borderRadius: 6,
              fontSize: 12, color: C.muted,
            }}>
              The user must sign in with their BNF Microsoft account. Their email must match exactly what is registered in Azure AD.
            </div>
          </div>
        )}

        {/* Role legend */}
        <div style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
          {[
            { role: 'admin', desc: 'Full access — manage users, create/delete/run reports', color: '#7c3aed', bg: '#ede9fe' },
            { role: 'user',  desc: 'Can view dashboard, run reports, and view logs',        color: '#0369a1', bg: '#e0f2fe' },
          ].map(r => (
            <div key={r.role} style={{
              background: '#fff', border: `1px solid ${r.bg}`,
              borderRadius: 8, padding: '10px 16px',
              display: 'flex', alignItems: 'center', gap: 10,
              flex: 1,
            }}>
              <span style={{ background: r.bg, color: r.color, padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 700 }}>
                {r.role}
              </span>
              <span style={{ fontSize: 12, color: C.muted }}>{r.desc}</span>
            </div>
          ))}
        </div>

        {/* Users table */}
        <div style={{ background: '#fff', borderRadius: 12, boxShadow: '0 1px 4px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
          {loading ? (
            <div style={{ padding: '40px', textAlign: 'center', color: C.muted }}>Loading…</div>
          ) : users.length === 0 ? (
            <div style={{ padding: '40px', textAlign: 'center', color: C.muted, fontSize: 14 }}>
              No users yet.
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: C.teal }}>
                  {['Name', 'Email', 'Role', 'Added by', 'Added on', isAdmin ? 'Actions' : ''].map(h => (
                    <th key={h} style={{
                      textAlign: 'left', padding: '12px 16px',
                      color: h === 'Role' ? C.yellow : '#fff',
                      fontWeight: 600, fontSize: 12,
                    }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {users.map(u => (
                  <tr key={u.email} style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <td style={{ padding: '14px 16px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{
                          width: 32, height: 32, borderRadius: '50%',
                          background: u.role === 'admin' ? '#ede9fe' : '#e0f2fe',
                          color: u.role === 'admin' ? '#7c3aed' : '#0369a1',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontWeight: 700, fontSize: 13, flexShrink: 0,
                        }}>
                          {u.name.charAt(0).toUpperCase()}
                        </div>
                        <span style={{ fontWeight: 600, color: C.teal }}>{u.name}</span>
                      </div>
                    </td>
                    <td style={{ padding: '14px 16px', color: C.muted }}>{u.email}</td>
                    <td style={{ padding: '14px 16px' }}>
                      {isAdmin ? (
                        <select
                          value={u.role}
                          onChange={e => handleRoleChange(u.email, e.target.value)}
                          style={{
                            border: '1px solid #d1d5db', borderRadius: 5,
                            padding: '4px 8px', fontSize: 12, fontWeight: 600,
                            color: u.role === 'admin' ? '#7c3aed' : '#0369a1',
                            background: u.role === 'admin' ? '#ede9fe' : '#e0f2fe',
                            cursor: 'pointer',
                          }}
                        >
                          <option value="user">user</option>
                          <option value="admin">admin</option>
                        </select>
                      ) : (
                        <span style={{
                          background: u.role === 'admin' ? '#ede9fe' : '#e0f2fe',
                          color: u.role === 'admin' ? '#7c3aed' : '#0369a1',
                          padding: '2px 9px', borderRadius: 4, fontSize: 11, fontWeight: 700,
                        }}>{u.role}</span>
                      )}
                    </td>
                    <td style={{ padding: '14px 16px', color: C.muted, fontSize: 12 }}>{u.added_by}</td>
                    <td style={{ padding: '14px 16px', color: C.muted, fontSize: 12 }}>{fmt(u.added_at)}</td>
                    {isAdmin && (
                      <td style={{ padding: '14px 16px' }}>
                        <button
                          onClick={() => handleRemove(u.email, u.name)}
                          style={{
                            background: '#fee2e2', color: '#dc2626', border: 'none',
                            padding: '5px 12px', borderRadius: 5,
                            fontSize: 12, fontWeight: 600, cursor: 'pointer',
                          }}
                        >
                          Remove
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </main>
    </Layout>
  )
}

const labelStyle: React.CSSProperties = {
  display: 'block', fontSize: 12, fontWeight: 600,
  color: C.muted, marginBottom: 5,
}

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '9px 12px',
  border: '1px solid #d1d5db', borderRadius: 6,
  fontSize: 13, color: C.teal, outline: 'none',
  height: 40,
}
